import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {DetectMethodChanges, DetectSetterChanges} from '../utils';
import {DisabledDatesArg, makeExpirationDisabledDates} from './expiration-dates';
import {OpgDateSelection} from './opg-copilot-domain';

// The Date section's three UI modes (WI-3): 'loaded' = the panel's current expiration (default,
// no picker), 'single' = one tradeable date, 'range' = a tradeable-date range.
type OpgDateKind = 'loaded' | 'single' | 'range';

// OPG CoPilot date block (CR-20260710-001, sketch frame 02 + A5/WI-3): the
// [<loaded date>] | Single Date | Date Range picker rendered ONCE per tab — on Calls & Puts the
// date is SHARED across both sides, but that is the parent's concern; this block only edits one
// OpgDateSelection. The 'loaded' button carries NO picker: it means "the panel's current date" and
// its label IS that date (passed in as loadedDateText); it is the default so Run is date-valid on
// open without touching this section.
//
// Family recipe (donors: the retired hg-copilot-select-tab, adjustment-pricing-grid/
// copilot/apg-roll-positions-panel — duplicated, never imported; isolated-sibling rule):
//   - The pickers are TRADEABLE-date pickers, not calendar pickers: every dx-date-box gets
//     [disabledDates] built from the host's visible-filtered expiration keys via the sanctioned
//     shared DATE rule (copilot-shared/expiration-dates.ts, commit 02daab720). The
//     predicate is REBUILT (fresh function reference) on every availableExpirations change
//     so the widget reliably re-applies the option.
//   - Clicks-only: [acceptCustomValue]=false + [openOnFieldClick]=true — typing disabled,
//     the calendar dropdown opens by click only.
//   - Chain not arrived yet (empty expiration list): the shared predicate falls back to
//     weekends-only and a loading hint row tells the user the full list is coming.
//
// Two-way contract: [(value)] with OpgDateSelection | null. The setter is a SINK — it syncs
// the internal fields and NEVER emits (echo-loop hazard); valueChange fires only from
// user-driven handlers, and the dx-date-box handlers guard on $event.event (user-initiated
// DevExtreme events carry it; programmatic write-backs do not). A structurally complete
// selection emits the well-formed OpgDateSelection; an incomplete one emits null. Semantic
// validity (range start < end, tradeable-date resolution) stays in opg-copilot-questionnaire —
// this block never judges (courier, spec §6).
@Component({
    selector: 'ets-opg-copilot-date-block',
    templateUrl: './opg-copilot-date-block.component.html',
    styleUrls: ['./opg-copilot-date-block.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpgCopilotDateBlockComponent {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {
    }

    // ---- contract ---------------------------------------------------------------------

    // Two-way sink: apply the parent's selection to the internal fields WITHOUT emitting.
    // The echo guard keeps a banana-in-box parent from disturbing an in-progress edit: when
    // the incoming value equals what this block last emitted (including null while a range
    // is half-filled), the write is our own emission reflected back — ignore it.
    @Input()
    @DetectSetterChanges()
    set value(v: OpgDateSelection | null) {
        if (this.isSameSelection(v || null, this.buildSelection())) {
            return;
        }
        if (v == null) {
            // Programmatic reset: clear every field, keep the mode.
            this.singleDate = null;
            this.rangeStart = null;
            this.rangeEnd = null;
            return;
        }
        if (v.kind === 'single') {
            this.dateKind = 'single';
            this.singleDate = v.date;
        } else if (v.kind === 'range') {
            this.dateKind = 'range';
            this.rangeStart = v.start;
            this.rangeEnd = v.end;
        } else if (v.kind === 'loaded') {
            this.dateKind = 'loaded';   // no picker fields — the panel's date is the value
        }
    }

    get value(): OpgDateSelection | null {
        return this.buildSelection();
    }

    @Output() valueChange = new EventEmitter<OpgDateSelection | null>();

    // 'yyyy-MM-dd' expiration keys, already visible-filtered by the host. Rebuilds the
    // disabledDates predicate on every change (fresh reference — see the shared helper).
    // Empty = chain not loaded yet -> weekend-only fallback + the loading row below.
    @Input()
    set availableExpirations(v: string[]) {
        this._availableExpirations = v || [];
        this.disabledDatesFn = makeExpirationDisabledDates(this._availableExpirations);
    }

    get availableExpirations(): string[] {
        return this._availableExpirations;
    }

    private _availableExpirations: string[] = [];

    // The panel's currently-loaded date, family-formatted by the orchestrator (or null when the
    // panel has no visible expiration yet). It is the FIRST date-kind button's label. Rebuilds the
    // (otherwise-stable) button list on change so dx-button-group re-renders only when the date
    // moves — mirrors the availableExpirations setter's rebuild-on-change recipe above.
    @Input()
    @DetectSetterChanges()
    set loadedDateText(v: string | null) {
        this._loadedDateText = v || null;
        this.dateKindButtons = this.buildDateKindButtons();
    }

    get loadedDateText(): string | null {
        return this._loadedDateText;
    }

    private _loadedDateText: string | null = null;

    @Input() disabled = false;

    // CR-20260831-004 round 4 (owner smoke walkthrough): the tab's SENT date — the baseline of the
    // current run — pushed down so this block computes its own changed-vs-run cues from its
    // INTERNAL mode state. The orchestrator's diff cannot do it: an incomplete selection (a mode
    // just switched, boxes still empty) reaches the model as NULL, the mode is lost, and the diff
    // mis-attributed the change to the value ("the start/end highlighted... and the date range
    // itself is not glowing yellow" — exactly backwards). This component always knows its mode.
    // null = no run baseline => no cues (nothing can be "changed since the run").
    @Input() baseline: OpgDateSelection | null = null;

    // The mode toggle's cue: the selected mode is not the mode the run was sent with. Paints the
    // SELECTED segment yellow; while it holds, the value boxes stay clean (no baseline for them).
    get kindChanged(): boolean {
        return this.baseline != null && this.baseline.kind !== this.dateKind;
    }

    // The value boxes' cue: same mode as the run, different (or cleared) date value(s).
    get valueChanged(): boolean {
        const b = this.baseline;
        if (!b || b.kind !== this.dateKind) {
            return false;
        }
        if (b.kind === 'single') {
            return !nullSameDate(b.date, this.singleDate);
        }
        if (b.kind === 'range') {
            return !nullSameDate(b.start, this.rangeStart) || !nullSameDate(b.end, this.rangeEnd);
        }
        return false;   // 'loaded' carries no fields
    }

    // ---- view state --------------------------------------------------------------------

    // dx-date-box [disabledDates] predicate: only listed expirations are clickable (weekends +
    // non-expiration days disabled). Seeded to the pre-load fallback (weekends only).
    disabledDatesFn: (arg: DisabledDatesArg) => boolean = makeExpirationDisabledDates([]);

    // Date kind is a SEGMENTED BUTTON SET spanning the full dialog width (family control
    // ruling + owner sketch review 2026-07-10). Per-item elementAttr carries the sketch's
    // exact data-element names onto the rendered segment buttons. The FIRST button is the
    // 'loaded' mode (WI-3) — its text is the loaded date itself (rebuilt on loadedDateText change,
    // NOT readonly). '—' placeholder until the panel reports a date.
    dateKindButtons: { id: OpgDateKind; text: string; elementAttr: { [key: string]: string } }[] =
        this.buildDateKindButtons();

    // Rebuild-survivable selection pair (CR-20260831-004 defect round 2). dateKindButtons is
    // REBUILT whenever the loaded-date label moves, and on every items rebuild dx-button-group
    // re-feeds its stale self-synced `selectedItems` OBJECT list into the fresh button collection
    // (button_group.js _renderButtons) — fighting our [selectedItemKeys] and producing the
    // owner-reported mutual-exclusivity bug: two segments highlighted at once, or none while one
    // is selected. Binding [selectedItems] to the CURRENT items array's selected object makes the
    // two sources agree by construction. Memoized on (items reference, kind) so the binding stays
    // reference-stable between real changes.
    private _dateKindSelItemsRef: { id: OpgDateKind; text: string; elementAttr: { [key: string]: string } }[] | null = null;
    private _dateKindSelKind: OpgDateKind | null = null;
    private _dateKindSelCache: { id: OpgDateKind; text: string; elementAttr: { [key: string]: string } }[] = [];

    get dateKindSelectedItems(): { id: OpgDateKind; text: string; elementAttr: { [key: string]: string } }[] {
        if (this._dateKindSelItemsRef !== this.dateKindButtons || this._dateKindSelKind !== this.dateKind) {
            this._dateKindSelItemsRef = this.dateKindButtons;
            this._dateKindSelKind = this.dateKind;
            this._dateKindSelCache = this.dateKindButtons.filter(b => b.id === this.dateKind);
        }
        return this._dateKindSelCache;
    }

    // Default 'loaded' (WI-3): the panel's current date, pre-selected, no picker to fill.
    dateKind: OpgDateKind = 'loaded';
    singleDate: Date | null = null;
    rangeStart: Date | null = null;
    rangeEnd: Date | null = null;

    // Chain not arrived yet -> the weekend-only fallback is in effect; show the loading hint
    // row until the host pushes the listed expirations (donor recipe, commit 02daab720).
    get expirationsLoading(): boolean {
        return this._availableExpirations.length === 0;
    }

    // ---- handlers (user-driven — the ONLY emit paths) ------------------------------------

    // dx-button-group click adapter (onItemClick fires on clicks only, never programmatically,
    // so no $event.event guard is needed here).
    onDateKindClick(e: { itemData: { id: OpgDateKind } }): void {
        this.onDateKind(e.itemData.id);
    }

    @DetectMethodChanges()
    onDateKind(kind: OpgDateKind): void {
        if (kind === this.dateKind) {
            return;   // re-click on the already-selected segment — nothing changed
        }
        this.dateKind = kind;
        this.emitSelection();
    }

    @DetectMethodChanges()
    onSingleDateChanged(e: { value?: Date; event?: unknown }): void {
        if (!e || !e.event) {
            return;   // programmatic write-back of [value] — never re-emit (echo-loop hazard)
        }
        this.singleDate = e.value instanceof Date ? e.value : null;
        this.emitSelection();
    }

    @DetectMethodChanges()
    onRangeStartChanged(e: { value?: Date; event?: unknown }): void {
        if (!e || !e.event) {
            return;
        }
        this.rangeStart = e.value instanceof Date ? e.value : null;
        this.emitSelection();
    }

    @DetectMethodChanges()
    onRangeEndChanged(e: { value?: Date; event?: unknown }): void {
        if (!e || !e.event) {
            return;
        }
        this.rangeEnd = e.value instanceof Date ? e.value : null;
        this.emitSelection();
    }

    // ---- internals -----------------------------------------------------------------------

    private emitSelection(): void {
        this.valueChange.emit(this.buildSelection());
    }

    // [<loaded date>] [Single Date] [Date Range] — rebuilt only when loadedDateText changes so the
    // dx-button-group binding stays a stable reference otherwise. '—' when no date is loaded yet
    // (the 'loaded' selection is Run-gated by the orchestrator in that state).
    private buildDateKindButtons(): { id: OpgDateKind; text: string; elementAttr: { [key: string]: string } }[] {
        return [
            {id: 'loaded', text: this._loadedDateText || '—', elementAttr: {'data-element': 'btn-loaded-date'}},
            {id: 'single', text: 'Single Date', elementAttr: {'data-element': 'btn-single-date'}},
            {id: 'range', text: 'Date Range', elementAttr: {'data-element': 'btn-date-range'}}
        ];
    }

    // A well-formed OpgDateSelection or null while incomplete. Per-mode fields are retained
    // when the user toggles modes (donor behavior) — only the ACTIVE mode's fields count.
    private buildSelection(): OpgDateSelection | null {
        if (this.dateKind === 'loaded') {
            return {kind: 'loaded'};   // structurally complete on its own; the orchestrator gates on the panel actually having a date
        }
        if (this.dateKind === 'single') {
            return this.isValidDate(this.singleDate)
                ? {kind: 'single', date: this.singleDate as Date}
                : null;
        }
        return (this.isValidDate(this.rangeStart) && this.isValidDate(this.rangeEnd))
            ? {kind: 'range', start: this.rangeStart as Date, end: this.rangeEnd as Date}
            : null;
    }

    private isSameSelection(a: OpgDateSelection | null, b: OpgDateSelection | null): boolean {
        if (a == null && b == null) {
            return true;
        }
        if (a == null || b == null) {
            return false;
        }
        if (a.kind !== b.kind) {
            return false;
        }
        if (a.kind === 'loaded') {
            return true;   // same kind (checked above), no fields to compare
        }
        if (a.kind === 'single' && b.kind === 'single') {
            return this.sameDate(a.date, b.date);
        }
        if (a.kind === 'range' && b.kind === 'range') {
            return this.sameDate(a.start, b.start) && this.sameDate(a.end, b.end);
        }
        return false;
    }

    private sameDate(x: Date, y: Date): boolean {
        return this.isValidDate(x) && this.isValidDate(y) && x.getTime() === y.getTime();
    }

    private isValidDate(d: Date | null): boolean {
        return d instanceof Date && !isNaN(d.getTime());
    }
}

// Null-tolerant date equality for the changed-vs-run cues (CR-20260831-004 round 4): two empty
// boxes are EQUAL; empty vs a sent date is a change (the sent value no longer stands).
function nullSameDate(x: Date | null, y: Date | null): boolean {
    const xv = x instanceof Date && !isNaN(x.getTime());
    const yv = y instanceof Date && !isNaN(y.getTime());
    if (!xv || !yv) {
        return xv === yv;
    }
    return (x as Date).getTime() === (y as Date).getTime();
}
